// The agent drawer (SPEC §11 + SPEC-UX-V03 §B, route #/agent/<id>[/<tab>]): a round avatar in the
// agent's shirt colour, NAME, role, department/floor, live status ("utilise ClickUp") and current
// task; the ⚙ header button (manage) opens Réglages; tabs Aperçu (default: what it does, « Je
// valide avant d'agir », connectors, next routines, last tasks), Routines (n), Tâches (n),
// Discussion (chat) and Réglages (manage level only). The composer (chat) and the save bar
// (settings) live in the drawer footer. The routines and the tasks of the agent are loaded once
// for the whole drawer (tab counts, Aperçu, Routines) and followed live until it closes.
import { h, icon, badge, tabs, emptyState, spinner, statusDot, classNames, replaceChildren, iconButton } from '../dom.js';
import { t } from '../i18n/fr.js';
import { agentAvatar } from './agent/avatar.js';
import { resolveTab, statusInfo, engineLabel, tabCount } from './agent/model.js';
import { createChatTab } from './agent/chat.js';
import { createTasksTab } from './agent/tasks.js';
import { createSettingsTab } from './agent/settings.js';
import { createOverviewTab, createAgentTaskFeed, FEED_LIMIT } from './agent/overview.js';
import { createRoutinesTab } from './agent/routines.js';
import { createRoutineSource } from './automations/routinelist.js';
import './agent/strings.js';

/**
 * Asks before closing the drawer while the settings have unsaved changes (× button, backdrop,
 * Escape). Route changes still close it (the router owns them).
 * @param {object} ctx
 * @param {object} layer
 * @param {() => boolean} isDirty
 * @returns {() => void} remover
 */
function guardUnsaved(ctx, layer, isDirty) {
  let asking = false;
  const ask = async () => {
    if (asking) return;
    asking = true;
    const ok = await ctx.overlay.confirm({
      title: t('ad.unsaved.title'), message: t('ad.unsaved.text'), confirmLabel: t('ad.unsaved.ok'), cancelLabel: t('ad.unsaved.keep'), danger: true,
    });
    asking = false;
    if (ok) layer.close('button');
  };
  const onClick = (e) => {
    if (!isDirty()) return;
    const target = e.target;
    const isClose = target?.closest?.('.layer-close') && layer.header?.contains?.(target);
    const isBackdrop = target?.classList?.contains?.('layer-backdrop') && target.parentNode === layer.el;
    if (!isClose && !isBackdrop) return;
    e.stopPropagation();
    e.preventDefault();
    ask();
  };
  const onKey = (e) => {
    if (e.key !== 'Escape' || !isDirty() || ctx.overlay.top?.() !== layer) return;
    e.stopPropagation();
    e.preventDefault();
    ask();
  };
  const win = globalThis.window;
  layer.el?.addEventListener?.('click', onClick, true);
  win?.addEventListener?.('keydown', onKey, true);
  return () => {
    layer.el?.removeEventListener?.('click', onClick, true);
    win?.removeEventListener?.('keydown', onKey, true);
  };
}

/**
 * Opens the drawer of an agent (route #/agent/<id>[/<tab>]).
 * @param {object} ctx app context
 * @param {string} agentId
 * @param {'overview'|'routines'|'tasks'|'chat'|'settings'|null} [tab] initial tab (default 'overview')
 * @returns {import('./overlay.js').Layer}
 */
export function openAgentDrawer(ctx, agentId, tab = 'overview') {
  const id = String(agentId ?? '');
  const findAgent = () => ctx.store.get().office.agents.find((a) => a.id === id) || null;
  let agent = findAgent();

  const layer = ctx.overlay.openDrawer({
    eyebrow: '',
    title: agent?.name || '',
    subtitle: agent?.role || '',
    size: 'lg',
    className: 'ad-drawer',
    content: h('div', { class: 'ad-loading' }, spinner(t('common.loading'))),
  });

  let closed = false;
  let built = false;
  let avatarEl = null;
  let avatarKey = '';
  let statusKey = '';
  let current = null;
  const panes = new Map();   // tab id → {tab api, pane element}
  const offs = [];
  let tabsEl = null;
  let statusEl = null;
  let gearBtn = null;
  let routines = null;     // the agent's routines (shared by Aperçu, Routines and the tab count)
  let feed = null;         // the agent's tasks (Aperçu and the tab count)
  let unguard = () => {};

  const scope = () => ({ floorId: agent?.floorId, deptId: agent?.deptId });
  const isAdmin = () => ctx.store.get().me?.role === 'admin';

  function showMessage(iconName, title, text) {
    layer.setContent(h('div', { class: 'ad-message' }, emptyState({ icon: iconName, title, text })));
    layer.setFooter?.();
  }

  function names() {
    const { office } = ctx.store.get();
    const dept = office.departments.find((d) => d.id === agent.deptId);
    const floor = office.floors.find((f) => f.id === agent.floorId);
    return { dept: dept?.name || '', floor: floor?.name || '' };
  }

  function renderHeader() {
    const n = names();
    layer.setTitle(agent.name || id);
    layer.setSubtitle?.(agent.role || '');
    layer.setEyebrow?.(n.dept || n.floor ? t('ad.eyebrow', { dept: n.dept, floor: n.floor }).replace(/^ · | · $/g, '') : '');
    const st = ctx.store.get().agentStatus?.get?.(agent.id) || agent.status || null;
    const info = statusInfo(st, ctx.store.get().connectors || []);
    const key = JSON.stringify([info.status, agent.look || null, agent.name]);
    if (avatarEl && key === avatarKey) return;
    avatarKey = key;
    const next = agentAvatar(agent, { size: 56, status: info.status, label: true, class: 'ad-head-avatar' });
    const parent = avatarEl?.parentNode;
    if (parent) { parent.insertBefore(next, avatarEl); avatarEl.remove(); } else if (layer.header) layer.header.insertBefore(next, layer.header.firstChild);
    avatarEl = next;
  }

  /**
   * The ⚙ header button (manage level): all the settings in one click. It replaces the old
   * « Modifier » button and the rename pencil (the name is the first field of Réglages).
   */
  function renderGear() {
    if (!ctx.can('manage', scope())) {
      if (gearBtn) { gearBtn = null; layer.setActions?.(); }
      return;
    }
    const label = t('ad.gear', { name: agent.name || id });
    if (!gearBtn) {
      gearBtn = iconButton('settings', label, { class: 'ad-gear', onClick: () => switchTo('settings') });
      layer.setActions?.(gearBtn);
    } else {
      gearBtn.setAttribute('aria-label', label);
      gearBtn.setAttribute('title', label);
    }
  }

  function renderStatus() {
    if (!statusEl) return;
    const { agentStatus, connectors, tasks } = ctx.store.get();
    const st = agentStatus?.get?.(agent.id) || agent.status || null;
    const info = statusInfo(st, connectors || []);
    const task = info.taskId ? tasks?.get?.(info.taskId) : null;
    const taskTitle = task?.title || '';
    const key = JSON.stringify([info.status, info.activity, info.taskId, taskTitle, !!agent.lead, agent.autonomy, agent.engine]);
    if (key === statusKey) return;
    statusKey = key;
    replaceChildren(statusEl,
      h('span', { class: classNames(['ad-status', `is-${info.status}`]) },
        statusDot(info.status),
        h('span', { class: 'ad-status-label' }, info.label),
        info.activity ? h('span', { class: 'ad-status-activity' }, `· ${info.activity}`) : null),
      info.taskId ? h('button', {
        type: 'button', class: 'btn btn-link btn-sm ad-current-task',
        'aria-label': t('ad.currentTaskAria', { title: taskTitle || info.taskId }),
        onClick: () => ctx.navigate(`#/task/${encodeURIComponent(info.taskId)}`),
      }, h('span', { class: 'btn-label' }, t(info.status === 'waiting' ? 'ad.waitingTask' : 'ad.currentTask', { title: taskTitle || '…' })),
      icon('arrowRight', { size: 13 })) : null,
      h('span', { class: 'spacer' }),
      agent.lead ? badge(t('ad.lead'), 'dark') : null,
      // The engine behind the agent (SPEC §11 C6): Claude, Codex (ChatGPT), Images OpenAI or Gemini.
      badge(engineLabel(agent.engine), 'info', { class: 'ad-engine-badge', title: t('ad.engineBadge', { engine: engineLabel(agent.engine) }) }),
      badge(agent.autonomy === 'auto' ? t('ad.autonomy.auto') : t('ad.autonomy.approve'), agent.autonomy === 'auto' ? 'amber' : 'neutral', { class: 'ad-autonomy-badge' }));
  }

  /** « Routines (n) » and « Tâches (n) » from the drawer's sources ('' while unknown). */
  function renderCounts() {
    if (!tabsEl || closed) return;
    tabsEl.setCount('routines', routines?.status === 'ok' ? tabCount(routines.items.length) : null);
    tabsEl.setCount('tasks', feed && feed.status !== 'loading' ? tabCount(feed.tasks.size, feed.capped ? FEED_LIMIT : Infinity) : null);
  }

  /** Switches tab from inside the drawer (Aperçu links); `section` scrolls Réglages to it. */
  function openTab(tabId, { section } = {}) {
    const target = resolveTab(tabId, { canManage: ctx.can('manage', scope()) });
    switchTo(target);
    if (target === 'settings' && section) panes.get('settings')?.api.showSection?.(section);
  }

  function paneFor(tabId) {
    if (panes.has(tabId)) return panes.get(tabId);
    let api;
    if (tabId === 'overview') api = createOverviewTab(ctx, agent, {
      routines, feed, canManage: ctx.can('manage', scope()), canOperate: ctx.can('operate', scope()), onTab: openTab,
    });
    else if (tabId === 'routines') api = createRoutinesTab(ctx, agent, { routines });
    else if (tabId === 'chat') api = createChatTab(ctx, agent, { canChat: ctx.can('operate', scope()), canManage: ctx.can('manage', scope()), scroller: layer.body });
    else if (tabId === 'tasks') api = createTasksTab(ctx, agent);
    else api = createSettingsTab(ctx, agent, { isAdmin: isAdmin(), onDeleted: () => layer.close('api') });
    const pane = h('div', { class: 'ad-pane', role: 'tabpanel', dataset: { tab: tabId } }, api.el);
    const tabBtn = [...(tabsEl?.childNodes || [])].find((b) => b.dataset?.tab === tabId);
    if (tabBtn) {
      pane.setAttribute('aria-labelledby', tabBtn.id);
      tabBtn.setAttribute('aria-controls', pane.id || (pane.id = `${tabBtn.id}-panel`));
    }
    const entry = { api, pane };
    panes.set(tabId, entry);
    return entry;
  }

  function switchTo(tabId) {
    if (closed) return;
    const entry = paneFor(tabId);
    if (current && current !== tabId) panes.get(current)?.api.deactivate?.();
    current = tabId;
    for (const [k, v] of panes) v.pane.hidden = k !== tabId;
    if (!entry.pane.parentNode) paneHost.appendChild(entry.pane);
    layer.el?.classList?.toggle?.('ad-is-chat', tabId === 'chat');
    layer.setFooter?.(entry.api.footer || null);
    tabsEl?.setActive?.(tabId);
    entry.api.activate?.();
  }

  const paneHost = h('div', { class: 'ad-panes' });

  /** Stops the drawer's sources and tabs (close, agent gone). */
  function teardown() {
    for (const { api } of panes.values()) { try { api.destroy?.(); } catch { /* ignore */ } }
    panes.clear();
    routines?.destroy();
    feed?.destroy();
    routines = null;
    feed = null;
  }

  function build() {
    built = true;
    const canManage = ctx.can('manage', scope());
    routines = createRoutineSource(ctx, { agentId: agent.id });
    feed = createAgentTaskFeed(ctx, agent.id);
    offs.push(routines.subscribe(renderCounts), feed.subscribe(renderCounts));
    const items = [
      { id: 'overview', label: t('agentdrawer.tabs.overview') },
      { id: 'routines', label: t('agentdrawer.tabs.routines'), count: null },
      { id: 'tasks', label: t('agentdrawer.tabs.tasks'), count: null },
      { id: 'chat', label: t('agentdrawer.tabs.chat') },
      ...(canManage ? [{ id: 'settings', label: t('agentdrawer.tabs.settings') }] : []),
    ];
    const first = resolveTab(tab, { canManage });
    tabsEl = tabs(items, { active: first, onChange: (v) => switchTo(v), label: t('ad.tabsLabel') });
    tabsEl.classList.add('ad-tabs');
    statusEl = h('div', { class: 'ad-statusbar', role: 'group', 'aria-label': t('ad.status.label') });
    statusKey = '';
    renderHeader();
    renderGear();
    renderStatus();
    renderCounts();
    layer.setContent(h('div', { class: 'ad-top' }, statusEl, tabsEl), paneHost);
    switchTo(first);
    unguard = guardUnsaved(ctx, layer, () => !!panes.get('settings')?.api.isDirty?.());
  }

  async function init() {
    if (!agent) {
      try {
        const res = await ctx.api.get(`/api/agents/${encodeURIComponent(id)}`);
        if (closed) return;
        if (res && typeof res === 'object' && res.id === id) agent = res;
      } catch (err) {
        if (closed) return;
        if (err?.status && err.status !== 404 && err.status !== 403) ctx.toast?.error?.(err);
      }
    }
    if (closed) return;
    if (!agent) {
      layer.setTitle(t('ad.notFound.title'));
      showMessage('user', t('ad.notFound.title'), t('ad.notFound.text'));
      return;
    }
    build();
  }

  // Live: status, renames, deletion.
  if (ctx.store.on) {
    offs.push(ctx.store.on('agentStatus', () => { if (built && !closed) { renderStatus(); renderHeader(); } }));
    offs.push(ctx.store.on('tasks', () => { if (built && !closed && statusEl) renderStatus(); }));
    offs.push(ctx.store.on('office', () => {
      if (!built || closed) return;
      const next = findAgent();
      if (!next) {
        teardown();
        built = false;
        if (gearBtn) { gearBtn = null; layer.setActions?.(); }
        showMessage('user', t('ad.notFound.title'), t('ad.notFound.text'));
        return;
      }
      agent = { ...agent, ...next };
      renderHeader();
      renderGear();
      renderStatus();
      panes.get('overview')?.api.update?.(agent);
    }));
  }

  layer.onClose(() => {
    closed = true;
    unguard();
    teardown();
    for (const off of offs.splice(0)) { try { off?.(); } catch { /* ignore */ } }
  });

  if (agent) build();
  else init();
  return layer;
}
